import { AlertCircle, Inbox, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Button } from './Button'

/** F0-19 — the empty half of the Skeleton/Empty/Error triad. */
export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string
  description?: ReactNode
  action?: ReactNode
  icon?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border border-dashed border-border-strong',
        'bg-surface-raised px-6 py-12 text-center',
        className,
      )}
    >
      <span className="text-text-tertiary" aria-hidden>
        {icon ?? <Inbox className="size-6" />}
      </span>
      <div className="flex flex-col gap-1">
        <p className="text-body font-medium text-text-primary">{title}</p>
        {description && <p className="max-w-prose text-body-sm text-text-secondary">{description}</p>}
      </div>
      {action}
    </div>
  )
}

/**
 * F0-19 — error state.
 *
 * The message is required and the retry affordance is opt-out rather than
 * opt-in: a dead end with no way forward is the state users report as "the app
 * broke". Never renders a raw exception; `detail` is for a correlation id or a
 * server-supplied explanation.
 */
export function ErrorState({
  title = 'Something went wrong',
  description,
  detail,
  onRetry,
  retryLabel = 'Try again',
  className,
}: {
  title?: string
  description?: ReactNode
  detail?: string
  onRetry?: () => void
  retryLabel?: string
  className?: string
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border border-state-critical-border',
        'bg-state-critical-surface px-6 py-10 text-center',
        className,
      )}
    >
      <AlertCircle aria-hidden className="size-6 text-state-critical" />
      <div className="flex flex-col gap-1">
        <p className="text-body font-medium text-text-primary">{title}</p>
        {description && <p className="max-w-prose text-body-sm text-text-secondary">{description}</p>}
        {detail && (
          <p className="font-mono text-caption text-text-tertiary">Reference: {detail}</p>
        )}
      </div>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry} iconStart={<RefreshCw aria-hidden className="size-3.5" />}>
          {retryLabel}
        </Button>
      )}
    </div>
  )
}
